import { Helmet } from "react-helmet-async";
import { SITE_DEFAULTS, canonicalUrl } from "@/lib/seo";

/**
 * Per-route head tags for a public marketing route: a self-referencing
 * canonical, plus the title/description pair and their Open Graph and Twitter
 * mirrors.
 *
 * Render alongside a page's existing <Helmet> block — react-helmet-async merges
 * the tags from every mounted instance — or on its own for pages that set no
 * other head tags.
 *
 * These tags now reach non-JS crawlers: the public marketing routes are
 * prerendered at build time (scripts/prerender.mjs), which collects this
 * component's output server-side and writes it into the static HTML, replacing
 * the site-wide fallbacks in index.html. Leave `title`/`description` unset to
 * inherit those fallbacks — that is what the homepage does, since index.html's
 * defaults are the homepage's own.
 *
 * index.html deliberately carries no static <link rel="canonical">, since every
 * route that is not prerendered is served that same shell and a hardcoded one
 * would point them all at the homepage.
 *
 * Every tag below is emitted unconditionally, falling back to SITE_DEFAULTS
 * rather than being omitted. That is load-bearing for client-side navigation:
 * react-helmet-async only replaces head tags carrying its own `data-rh`
 * attribute, so index.html marks exactly this set as helmet-owned. Helmet then
 * adopts them on the first commit and swaps them on every route change, instead
 * of appending a second copy behind the stale one. A tag this component stops
 * emitting is a tag helmet would delete — so the set here and the `data-rh`
 * attributes in index.html have to stay in lockstep. An omitted title was also
 * a bug in its own right: helmet's updateTitle no-ops on an undefined title, so
 * navigating back to the homepage kept the previous route's tab title.
 */

type CanonicalTagsProps = {
  /** Route path, leading slash included. "/" for the homepage. */
  path: string;
  /** Page title. Also emitted as og:title and twitter:title (the site default has its own sharing title). */
  title?: string;
  /** Meta description. Also emitted as og:description and twitter:description. */
  description?: string;
};

const CanonicalTags = ({ path, title, description }: CanonicalTagsProps) => {
  const url = canonicalUrl(path);
  const pageTitle = title ?? SITE_DEFAULTS.title;
  const metaDescription = description ?? SITE_DEFAULTS.description;
  // A route's own title and description double as its social copy; only the
  // site-wide fallback has separate sharing copy for scrapers.
  const socialTitle = title ?? SITE_DEFAULTS.socialTitle;
  const socialDescription = description ?? SITE_DEFAULTS.socialDescription;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={socialDescription} />
      <meta property="og:url" content={url} />
      <meta name="twitter:title" content={socialTitle} />
      <meta name="twitter:description" content={socialDescription} />
      <link rel="canonical" href={url} />
    </Helmet>
  );
};

export default CanonicalTags;
